import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Heatmap from './Heatmap';
import TopTracks from './TopTracks';
import MobileShareFAB from './MobileShareFAB';
import SkeletonLoader from './SkeletonLoader';
import AdBanner from './AdBanner';
import { useAppContext } from '../context/AppContext';
import { Platform } from '../types';

interface ResultsProps {}

const Results: React.FC<ResultsProps> = () => {
  const resultsBlockRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { playlistId } = useParams<{ playlistId?: string }>();
  const [searchParams] = useSearchParams();
  const { audioFeatures, playlistInfo, currentPlaylistId, analyzePlaylist, clearPlaylist, retryAnalysis, platform, isAnalyzing, analysisStep, error } = useAppContext();
  const [triggerShare, setTriggerShare] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Re-hydrate from a shared/deep link. When the URL carries a playlist id that
  // isn't already loaded (cold load, refresh, or a link opened by someone else),
  // kick off analysis for it. Guards prevent re-triggering while a run is in flight.
  useEffect(() => {
    if (!playlistId) return;
    if (isAnalyzing) return;
    if (currentPlaylistId === playlistId && (audioFeatures.length > 0 || error)) return;
    if (audioFeatures.length > 0 && currentPlaylistId === playlistId) return;
    const platformParam = searchParams.get('platform');
    const urlPlatform: Platform = platformParam === 'ytmusic' ? 'ytmusic' : platformParam === 'apple' ? 'apple' : 'spotify';
    analyzePlaylist({ platform: urlPlatform, playlistId, originalUrl: '', storefront: searchParams.get('sf') || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  const handleMobileShare = () => {
    setTriggerShare(true);
    // Reset after a short delay so it can be triggered again
    setTimeout(() => setTriggerShare(false), 100);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.background.default,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated lava lamp background */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isDark ? 0.25 : 0.15,
          pointerEvents: 'none',
          filter: 'blur(50px)',
          '& svg': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
          <defs>
            <radialGradient id="r-blob1" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#3B82F6' : '#a8e6cf'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="8s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#3B82F6' : '#a8e6cf'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="r-blob2" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#4338CA' : '#ffe66d'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="10s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#4338CA' : '#ffe66d'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="r-blob3" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#6366F1' : '#ffaa66'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="12s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#6366F1' : '#ffaa66'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="r-blob4" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#1E1B4B' : '#ff8b94'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="9s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#1E1B4B' : '#ff8b94'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="r-blob5" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#2563EB' : '#4ecdc4'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="11s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#2563EB' : '#4ecdc4'} stopOpacity="0" />
            </radialGradient>

            <radialGradient id="r-blob6" cx="50%" cy="50%">
              <stop offset="0%" stopColor={isDark ? '#7C3AED' : '#dda8ff'} stopOpacity="0.9">
                <animate attributeName="stop-opacity" values="0.9;0.5;0.9" dur="13s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor={isDark ? '#7C3AED' : '#dda8ff'} stopOpacity="0" />
            </radialGradient>
          </defs>
          
          <circle fill="url(#r-blob1)" r="220">
            <animate attributeName="cx" values="200;800;200" dur="25s" repeatCount="indefinite" />
            <animate attributeName="cy" values="300;200;300" dur="20s" repeatCount="indefinite" />
            <animate attributeName="r" values="220;300;220" dur="15s" repeatCount="indefinite" />
          </circle>

          <circle fill="url(#r-blob2)" r="280">
            <animate attributeName="cx" values="800;300;800" dur="30s" repeatCount="indefinite" />
            <animate attributeName="cy" values="400;700;400" dur="25s" repeatCount="indefinite" />
            <animate attributeName="r" values="280;200;280" dur="18s" repeatCount="indefinite" />
          </circle>

          <circle fill="url(#r-blob3)" r="250">
            <animate attributeName="cx" values="500;700;500" dur="28s" repeatCount="indefinite" />
            <animate attributeName="cy" values="600;300;600" dur="22s" repeatCount="indefinite" />
            <animate attributeName="r" values="250;330;250" dur="16s" repeatCount="indefinite" />
          </circle>

          <circle fill="url(#r-blob4)" r="200">
            <animate attributeName="cx" values="300;600;300" dur="32s" repeatCount="indefinite" />
            <animate attributeName="cy" values="200;600;200" dur="27s" repeatCount="indefinite" />
            <animate attributeName="r" values="200;270;200" dur="19s" repeatCount="indefinite" />
          </circle>

          <circle fill="url(#r-blob5)" r="240">
            <animate attributeName="cx" values="700;200;700" dur="26s" repeatCount="indefinite" />
            <animate attributeName="cy" values="700;400;700" dur="24s" repeatCount="indefinite" />
            <animate attributeName="r" values="240;310;240" dur="17s" repeatCount="indefinite" />
          </circle>

          <circle fill="url(#r-blob6)" r="210">
            <animate attributeName="cx" values="400;900;400" dur="29s" repeatCount="indefinite" />
            <animate attributeName="cy" values="500;500;500" dur="23s" repeatCount="indefinite" />
            <animate attributeName="r" values="210;290;210" dur="20s" repeatCount="indefinite" />
          </circle>
        </svg>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          width: '100%',
          overflowY: 'auto',
        }}
      >
        <Box
          ref={resultsBlockRef}
          sx={{
            maxWidth: '1200px',
            mx: 'auto',
            px: { xs: 3, sm: 4, md: 6 },
            py: { xs: 4, sm: 6, md: 10 },
          }}
        >
          {isAnalyzing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <SkeletonLoader progress={analysisStep} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 10, maxWidth: '480px', mx: 'auto' }}>
              <Typography sx={{ color: theme.palette.text.primary, fontSize: '20px', fontWeight: 700, mb: 1 }}>
                That didn't work
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: '15px', lineHeight: 1.6, mb: 3 }}>
                {error}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* Primary: re-run the same playlist — recovers transient failures (e.g. a 502) in one tap */}
                <Box
                  component="button"
                  onClick={() => { retryAnalysis(); }}
                  sx={{
                    px: 3, py: 1.25, borderRadius: '100px', border: 'none',
                    background: isDark ? '#fff' : '#000',
                    color: isDark ? '#000' : '#fff',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.2)' },
                    '&:active': { transform: 'scale(0.97)' },
                  }}
                >
                  Try again
                </Box>
                <Box
                  component="button"
                  onClick={() => { clearPlaylist(); navigate('/'); }}
                  sx={{
                    px: 3, py: 1.25, borderRadius: '100px',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                    background: 'transparent', color: theme.palette.text.secondary,
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 0.2s ease',
                    '&:hover': { background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                  }}
                >
                  Analyze another playlist
                </Box>
              </Box>
            </Box>
          ) : (
          <>
          {/* Back button */}
          <Box sx={{ mb: 2 }}>
            <Box
              component="button"
              onClick={() => { clearPlaylist(); navigate('/'); }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: '100px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                background: 'transparent',
                color: theme.palette.text.secondary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              Analyze another playlist
            </Box>
          </Box>

          {/* Hero Section */}
          <Box sx={{ mb: { xs: 1, md: 2 }, textAlign: 'center' }}>
            {/* Playlist card - above heading */}
            {playlistInfo && (
              <Box
                component="a"
                href={platform === 'ytmusic'
                  ? `https://music.youtube.com/playlist?list=${playlistInfo.id}`
                  : platform === 'apple'
                    ? `https://music.apple.com/${searchParams.get('sf') || 'us'}/playlist/${playlistInfo.id}`
                    : `https://open.spotify.com/playlist/${playlistInfo.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 3,
                  py: 2,
                  borderRadius: '16px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                  background: theme.palette.background.paper,
                  boxShadow: isDark ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.06)',
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'all 0.2s ease',
                  mb: 3,
                  '&:hover': {
                    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {playlistInfo.image && (
                  <Box
                    component="img"
                    src={playlistInfo.image}
                    alt={playlistInfo.name}
                    sx={{
                      width: 64, height: 64,
                      borderRadius: '12px',
                      objectFit: 'cover',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                )}
                <Box sx={{ textAlign: 'left' }}>
                  <Typography sx={{ fontSize: '16px', fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                    {playlistInfo.name}
                  </Typography>
                  {playlistInfo.owner && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {platform === 'apple' ? (
                        <svg width="14" height="14" viewBox="0 0 384 512" fill={theme.palette.text.secondary} style={{ flexShrink: 0 }}>
                          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                        </svg>
                      ) : platform === 'ytmusic' ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.palette.text.secondary} style={{ flexShrink: 0 }}>
                          <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L16.2 12l-6.516 3.54z"/>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.palette.text.secondary} style={{ flexShrink: 0 }}>
                          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                        </svg>
                      )}
                      <Typography sx={{ fontSize: '13px', color: theme.palette.text.secondary }}>
                        playlist by {playlistInfo.owner}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Title - below card. The single display heading on this screen. */}
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '24px', sm: '32px', md: '42px' },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 2,
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
                maxWidth: '760px',
                mx: 'auto',
              }}
            >
              Emotional heatmap{playlistInfo?.name ? ` for "${playlistInfo.name}"` : ''}
            </Typography>

            {/* Blurb */}
            <Typography
              sx={{
                fontSize: { xs: '16px', md: '18px' },
                fontWeight: 400,
                color: theme.palette.text.secondary,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Discover the emotional depth of your playlist with the interactive heatmap below. Explore your music and get similar recommendations.
            </Typography>
          </Box>

          {/* Heatmap */}
          {audioFeatures.length > 0 ? (
            <Heatmap 
              data={[]} 
              audioFeatures={audioFeatures}
              triggerShare={triggerShare}
            />
          ) : (
            <Box 
              sx={{ 
                textAlign: 'center', 
                p: { xs: 4, md: 6 },
                borderRadius: '24px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                background: theme.palette.background.paper,
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  mb: 1,
                  fontWeight: 600,
                  fontSize: { xs: '16px', md: '20px' },
                }}
              >
                Audio features not available
              </Typography>
              <Typography 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '13px', md: '14px' },
                }}
              >
                Showing playlist tracks instead
              </Typography>
              <Box sx={{ mt: 4 }}>
                <TopTracks tracks={[]} features={audioFeatures} />
              </Box>
            </Box>
          )}

          {/* Ad Banner */}
          <AdBanner />
          </>
          )}
        </Box>
      </Box>

      {/* Mobile Share FAB */}
      <MobileShareFAB onClick={handleMobileShare} />
    </Box>
  );
};

export default Results;
